/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useEffect, useId, useState } from 'react';
import { useSelector } from 'react-redux';
import { SALES } from 'self-service-api/constants/ProductLines';
import styled from 'styled-components';
import SalesServiceSeatExplainerModal from 'ui-addon-upgrades/_core/common/components/seatsModals/SalesServiceSeatExplainerModal';
import { useAppSelector } from '../../../reducers';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { calculatedPriceForProductConfigurationsInListSelector } from '../../../selectors/PricingSelectors';
import { hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector, productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, productForSkuIdSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import { InterstitialSeatsTile } from '../../interstitial/InterstitialSeatsTile';
import { FlushedTitle, StyledStepperInput } from '../../plan-builder/SharedComponents';
import { FreeUsersDisclaimer } from '../FreeUsersDisclaimer';
import PriceDisplay from '../pricing/PriceDisplay';
import Subtext from '../text/Subtext';
import PaidUsersModal from './PaidUsersModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledInlineTitle = styled(FormattedMessage).withConfig({
  displayName: "SeatsPicker__StyledInlineTitle",
  componentId: "mt1c1c-0"
})(["font-weight:600;font-size:14px;"]);
const SeatsPicker = ({
  additionalUnitSkuId,
  additionalUnitQuantity,
  onChange,
  productConfigurations,
  hideFreeUsersDisclaimer = false,
  useProductLineSpecificTitle = true,
  className = '',
  compact,
  trackingSource,
  queryParamTerm,
  isInterstitialTile = false
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const isQuantityPack = useAppSelector(state => skuIdIsQuantityPackSelector(state, {
    skuId: additionalUnitSkuId
  }));
  const {
    includedWithProductVersion,
    unitType,
    unitScope,
    productVersionSkuId
  } = useAppSelector(state => {
    if (!isQuantityPack) {
      return {
        includedWithProductVersion: 0,
        unitType: null,
        unitScope: null,
        productVersionSkuId: additionalUnitSkuId
      };
    } else {
      const quantityPackSku = skuSelector(state, {
        skuId: additionalUnitSkuId
      });
      const {
        quantityUnit: {
          includedWithProductVersion: quantityPackIncludedwithProductVersion,
          unitType: quantityPackUnitType,
          unitScope: quantityPackUnitScope
        },
        productVersionSkuId: quantityPackProductVersionSkuId
      } = quantityPackSku;
      return {
        includedWithProductVersion: quantityPackIncludedwithProductVersion,
        unitType: quantityPackUnitType,
        unitScope: quantityPackUnitScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      };
    }
  });
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productVersionSkuId
  }));
  const currencyCode = useSelector(activeCurrencySelector);
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId: productVersionSkuId
  }));
  const ownedQuantity = useAppSelector(state => unitType && unitScope ? productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    unitType,
    unitScope
  }) : productsFromProductInventoryQuantitySelector(state, {
    skuId: additionalUnitSkuId
  }));
  const minPurchasableSeats = isSeatsRedesignUngated ? ownedQuantity === 0 ? 1 : ownedQuantity + includedWithProductVersion : includedWithProductVersion + ownedQuantity;
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      apiName
    }
  }));
  const ownedUnitQuantity = ownsProduct ? minPurchasableSeats : 0;
  const [paidUsersModalOpen, setPaidUsersModalOpen] = useState(false);
  const [salesServiceExplainerModalOpen, setSalesServiceExplainerModalOpen] = useState(false);
  const handlePaidUsersLinkClick = () => {
    setPaidUsersModalOpen(true);
    getUsageTracker().track('clickToViewModal', {
      name: 'paid-vs-free-users-modal',
      productLine,
      productLevel,
      location: trackingSource
    });
  };
  useEffect(() => {
    if (additionalUnitQuantity < ownedQuantity) {
      onChange(ownedQuantity);
    }
  }, [additionalUnitQuantity, ownedQuantity]);
  const additionalUnitPrice = useAppSelector(state => calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations: productConfigurations,
    productConfigurations: [createProductConfig({
      skuId: additionalUnitSkuId
    })],
    queryParamTerm
  }).monthly.calculatedPrice);
  const roundValue = ({
    target: {
      value
    }
  }) => {
    const roundedValue = parseInt(value, 10);
    let nextAdditionalUnitQuantity = roundedValue < minPurchasableSeats ? minPurchasableSeats : roundedValue;
    nextAdditionalUnitQuantity = nextAdditionalUnitQuantity - includedWithProductVersion;
    onChange(nextAdditionalUnitQuantity);
  };
  const handleSeatsChange = event => {
    if (event.target.value) {
      getUsageTracker().track('additionalUnitsPicker', {
        trackingSource,
        unitType,
        productLine,
        productLevel,
        ownsProduct,
        skuId: additionalUnitSkuId,
        ownedUnitQuantity,
        displayedUnitQuantity: event.target.value
      });
    }
    roundValue(event);
  };
  const seatsPickerTitleId = useId();
  const seatsPickerSubtextId = useId();
  const ariaDescribedBy = [seatsPickerTitleId, seatsPickerSubtextId].join(' ');
  // productLine will be filled in for a la carte hubs and unbundled suite
  // For TB sku, there's no productLine. Use quantityPack's unitScope to effectively
  // get its productLine
  const productLineKey = productLine || unitScope;
  const handleSalesServiceSeatsLinkClick = () => {
    setSalesServiceExplainerModalOpen(true);
    getUsageTracker().track('clickToViewModal', {
      name: `${productLineKey.toLowerCase()}-seat-explainer-modal`,
      productLevel,
      location: trackingSource
    });
  };
  const handleSalesServiceSeatsModalClose = () => {
    setSalesServiceExplainerModalOpen(false);
    getUsageTracker().track('modalInteraction', {
      modalName: `${productLineKey.toLowerCase()}-seat-explainer-modal`,
      action: 'closed',
      productLevel
    });
  };
  if (compact) {
    return isSeatsRedesignUngated ? null : /*#__PURE__*/_jsxs("div", {
      className: className,
      children: [/*#__PURE__*/_jsx("span", {
        id: seatsPickerTitleId,
        children: /*#__PURE__*/_jsx(StyledInlineTitle, {
          message: `pricingPage.seatsPicker.title.${productLevel.toLowerCase()}`
        })
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        align: "center",
        children: [/*#__PURE__*/_jsxs(Subtext, {
          children: [/*#__PURE__*/_jsx("span", {
            id: seatsPickerSubtextId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "pricingPage.seatsPicker.compactSubtext_jsx",
              elements: {
                PriceDisplay
              },
              options: {
                currencyCode,
                price: additionalUnitPrice
              }
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(UILink, {
                onClick: handlePaidUsersLinkClick,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "pricingPage.seatsPicker.howMany"
                })
              })
            })
          })]
        }), paidUsersModalOpen && /*#__PURE__*/_jsx(PaidUsersModal, {
          productLevel: productLevel,
          productLine: productLine,
          onCloseClick: () => setPaidUsersModalOpen(false),
          hideFreeUsersDisclaimer: hideFreeUsersDisclaimer
        }), /*#__PURE__*/_jsx(UIStepperInput, {
          "aria-label": I18n.text('pricingPage.seatsPicker.a11y.paidUsers'),
          "aria-describedby": ariaDescribedBy,
          style: {
            minWidth: '60px',
            width: '100px'
          },
          value: additionalUnitQuantity + includedWithProductVersion,
          min: minPurchasableSeats,
          onChange: handleSeatsChange,
          "data-selenium-class": "seats-picker-input",
          "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`
        })]
      })]
    });
  }
  if (isSeatsRedesignUngated) {
    if (isInterstitialTile) {
      return /*#__PURE__*/_jsx(InterstitialSeatsTile, {
        productLine: productLineKey,
        price: additionalUnitPrice,
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: productLineKey === SALES ? 'interstitial.salesSeats.description' : 'interstitial.serviceSeats.description'
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: productLineKey === SALES ? `interstitial.salesSeats.title.${productLevel}` : `interstitial.serviceSeats.title.${productLevel}`,
          options: {
            count: additionalUnitQuantity + includedWithProductVersion
          }
        }),
        children: /*#__PURE__*/_jsx(StyledStepperInput, {
          "aria-label": `pricingPage.seatsPicker.a11y.select.${productLineKey}`,
          "aria-describedby": ariaDescribedBy,
          value: additionalUnitQuantity + includedWithProductVersion,
          min: minPurchasableSeats,
          onChange: handleSeatsChange,
          "data-selenium-class": "seats-picker-input",
          "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`,
          disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "interstitial.seatsPicker.disabledMinButtonTooltip"
          }),
          disabledMinButtonTooltipProps: {
            placement: 'bottom'
          }
        })
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [salesServiceExplainerModalOpen && /*#__PURE__*/_jsx(SalesServiceSeatExplainerModal, {
        handleModalClose: handleSalesServiceSeatsModalClose,
        productLine: productLineKey
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        children: [/*#__PURE__*/_jsx(FlushedTitle, {
          "aria-level": 3,
          className: "m-bottom-2",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `pricingPage.seatsPicker.select.${productLineKey}_jsx`,
            elements: {
              Link: ({
                children
              }) => /*#__PURE__*/_jsx(UILink, {
                onClick: handleSalesServiceSeatsLinkClick,
                children: children
              })
            }
          })
        }), /*#__PURE__*/_jsx(StyledStepperInput, {
          "aria-label": `pricingPage.seatsPicker.a11y.select.${productLineKey}`,
          "aria-describedby": ariaDescribedBy,
          value: additionalUnitQuantity + includedWithProductVersion,
          min: minPurchasableSeats,
          onChange: handleSeatsChange,
          "data-selenium-class": "seats-picker-input",
          "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`,
          disabledMinButtonTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "interstitial.seatsPicker.disabledMinButtonTooltip"
          }),
          disabledMinButtonTooltipProps: {
            placement: 'bottom'
          }
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "m-bottom-4",
        children: /*#__PURE__*/_jsx(UIFlex, {
          direction: "column",
          children: /*#__PURE__*/_jsx("span", {
            id: seatsPickerSubtextId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: `pricingPage.seatsPicker.subtext.seats.${productLineKey}_jsx`,
              elements: {
                PriceDisplay
              },
              options: {
                currencyCode,
                price: additionalUnitPrice
              }
            })
          })
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    children: [useProductLineSpecificTitle ? [productLine, unitScope].includes(SALES) ? /*#__PURE__*/_jsx(StyledInlineTitle, {
      message: "pricingPage.seatsPicker.hubSpecificTitles.sales"
    }) : /*#__PURE__*/_jsx(StyledInlineTitle, {
      message: "pricingPage.seatsPicker.hubSpecificTitles.service"
    }) : /*#__PURE__*/_jsx(StyledInlineTitle, {
      message: "pricingPage.seatsPicker.howMany"
    }), /*#__PURE__*/_jsxs(UIFlex, {
      direction: "row",
      justify: "between",
      align: "center",
      children: [/*#__PURE__*/_jsx(Small, {
        style: {
          paddingRight: '15px'
        },
        children: /*#__PURE__*/_jsx(Subtext, {
          id: seatsPickerSubtextId,
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `pricingPage.seatsPicker.subtext.${billingNameKey}_jsx`,
            elements: {
              PriceDisplay
            },
            options: {
              currencyCode,
              price: additionalUnitPrice
            }
          })
        })
      }), /*#__PURE__*/_jsx(UIStepperInput, {
        "aria-label": I18n.text(`pricingPage.seatsPicker.hubSpecificTitles.${productLine.toLowerCase()}`),
        "aria-describedby": ariaDescribedBy,
        style: {
          minWidth: '60px',
          width: '100px'
        },
        value: additionalUnitQuantity + includedWithProductVersion,
        min: minPurchasableSeats,
        onChange: handleSeatsChange,
        "data-selenium-class": "seats-picker-input",
        "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`
      })]
    }), !hideFreeUsersDisclaimer && /*#__PURE__*/_jsx(FreeUsersDisclaimer, {})]
  });
};
export default SeatsPicker;